import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BackButton2 } from '../../../../common/BackButton2';
import IntegrationDataCard from '../../../client-setup/pages/account-integration/IntegrationDataCard';
import {
  connectZendeskCRM,
  disconnectIntegration,
  getListOfIntegration,
  intiateOAuthForCRM,
  intiateOAuthForDataSource,
  verifyDataSourceTokenInfo,
  verifyTokenInfo
} from '../../../../api/AccountSetup';
import ToastNotification from '../../../../common/ToastNotification';
import DataSourceInfoPopup from './DataSourceInfoPopup';
import AccountSetupButton from '../../../../common/AccountSetupButton';
import { useGuideContext } from '../../../../Contexts/GuidingContext';
import { useMenuContext } from '../../../../Contexts/MenuContext';
import { HOSPITALITY_LIST, OTHER_LIST, RETAILER_LIST } from '../DATA';

interface IntegrationProps {
  isSettings?: boolean;
}

export const Integration = (props: IntegrationProps) => {
  const { isSettings } = props;
  const navigate = useNavigate();
  const { setDisabled } = useGuideContext();
  const [searchParams] = useSearchParams();
  const { menuList } = useMenuContext();
  const code = searchParams.get('code');
  const companyUuid = sessionStorage.getItem('companyUuid');
  const businessType = sessionStorage.getItem('businessType');

  const [integrationList, setIntegrationList] = useState([]);
  const [showLoading, setShowLoading] = useState(true);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [modalInfo, setModalInfo] = useState({
    show: false,
    type: '',
    uuid: '',
    storeName: '',
    subDomain: '',
    userName: '',
    token: ''
  });

  const stepsList =
    businessType === 'Retail'
      ? RETAILER_LIST
      : businessType === 'Hospitality' && menuList.includes('Services')
        ? HOSPITALITY_LIST
        : OTHER_LIST;
  const stepNumber = stepsList.filter((data) => data.step === 'Integration')[0].id;

  const handleGoBack = () => {
    navigate('/account-setup/widgets');
  };

  const handleMoveToDashbord = () => {
    setDisabled(
      sessionStorage.getItem('businessType') === 'Retail' &&
        sessionStorage.getItem('guidance') === 'started'
        ? true
        : false
    );
    sessionStorage.setItem('setting', 'finished');
    navigate('/dashboard');
  };

  const fetchListOfIntegration = async () => {
    try {
      setShowLoading(true);
      const res = await getListOfIntegration(companyUuid ? companyUuid : '');
      if (res.payload) {
        setIntegrationList(res.payload.integrationPartyList);
      } else if (res.message) {
        ToastNotification({
          message: res.message,
          type: 'error'
        });
      }
      setShowLoading(false);
    } catch {
      setShowLoading(false);
    }
  };

  const handleCRMConnect = async (partyUuid: string, type: string) => {
    if (type === 'Freshdesk' || type === 'Zendesk') {
      handleModal(partyUuid, type);
    } else {
      try {
        sessionStorage.setItem('partyUuid', partyUuid);
        sessionStorage.setItem('shop', '');
        const resp = await intiateOAuthForCRM(
          partyUuid,
          companyUuid ? companyUuid : '',
          isSettings
            ? `${process.env.REACT_APP_URL}settings/integration`
            : `${process.env.REACT_APP_URL}account-setup/integration`
        );
        if (resp.message === 'success') {
          window.location.href = resp.payload.url;
        }
      } catch {}
    }
  };

  const handleDataSourceConnect = async () => {
    setSubmitDisabled(true);
    if (modalInfo.storeName) {
      try {
        sessionStorage.setItem('partyUuid', modalInfo.uuid);
        sessionStorage.setItem('shop', modalInfo.storeName);
        const resp = await intiateOAuthForDataSource(
          modalInfo.storeName,
          isSettings ? 'settings/integration' : 'account-setup/integration'
        );
        if (resp.message === 'success') {
          window.location.href = resp.payload.url;
        }
      } catch {}
    } else {
      ToastNotification({
        message: 'Please enter store name',
        type: 'error'
      });
    }
  };

  const handleVerifyTokenInfo = async (code: string) => {
    try {
      setShowLoading(true);
      const integrationPartyUuid = sessionStorage.getItem('partyUuid');
      const shopName = sessionStorage.getItem('shop');
      const resp =
        shopName === ''
          ? await verifyTokenInfo(
              integrationPartyUuid ? integrationPartyUuid : '',
              companyUuid ? companyUuid : '',
              code,
              isSettings
                ? `${process.env.REACT_APP_URL}settings/integration`
                : `${process.env.REACT_APP_URL}account-setup/integration`
            )
          : await verifyDataSourceTokenInfo(
              shopName || '',
              integrationPartyUuid ? integrationPartyUuid : '',
              companyUuid ? companyUuid : '',
              code,
              isSettings ? 'settings/integration' : 'account-setup/integration'
            );
      if (resp.message === 'success') {
        fetchListOfIntegration();
      } else {
        setShowLoading(false);
      }
    } catch {}
  };

  const handleDeskCRMConnect = async () => {
    let payload: any = {
      subdomain: modalInfo.subDomain,
      token: modalInfo.token
    };
    if (modalInfo.type === 'Zendesk') {
      payload = {
        ...payload,
        username: modalInfo.userName
      };
    }
    if (modalInfo.subDomain && modalInfo.token) {
      const resp = await connectZendeskCRM(modalInfo.uuid, companyUuid ? companyUuid : '', payload);
      const data = await resp.json();
      if (data.message === 'Success') {
        handleModal('', '');
        fetchListOfIntegration();
        ToastNotification({
          message: 'Connected successfully',
          type: 'success'
        });
      }
    } else {
      ToastNotification({
        message: 'Please enter valid info',
        type: 'error'
      });
    }
  };

  const handleModal = (partyUuid: string, type: string) => {
    setModalInfo({
      ...modalInfo,
      show: !modalInfo.show,
      uuid: partyUuid,
      type: type,
      storeName: '',
      subDomain: '',
      userName: '',
      token: ''
    });
  };

  useEffect(() => {
    fetchListOfIntegration();
  }, []);

  useEffect(() => {
    if (code) {
      handleVerifyTokenInfo(code);
    }
  }, [code]);

  const handelToDisconnect = async (info: any) => {
    if (info && info.accountIntegrationUuid) {
      const payload = {
        accountIntegrationUuid: info.accountIntegrationUuid
      };
      const response = await disconnectIntegration(payload);
      const data = await response.json();
      if (data.message === 'Successful') {
        fetchListOfIntegration();
        ToastNotification({
          message: 'Successfully disconnected',
          type: 'success'
        });
      }
    } else {
      ToastNotification({
        message: 'Something went wrong, Please try later',
        type: 'error'
      });
    }
  };

  return (
    <>
      <div
        className={`${isSettings ? '' : 'md:pt-3 lg:pt-5 p-2'} h-full w-full min-h-[600px] flex flex-col flex-1 justify-between px-2 md:px-0 lg:px-0`}
      >
        {!isSettings && (
          <div className="block">
            <h2 className="h-[31px] lg:ml-9 ml-[4px] md:ml-3  md:mb-[5px] text-[14px] sm:text-[18px] sm:leading-[26.86px]  md:text-base  md:leading-[30.86px] font-normal">
              STEP {stepNumber}
            </h2>
            <div className="flex ml-[4px] md:ml-3 lg:ml-1 lg:mt-1 md:mt-2">
              <div className="lg:mt-0 md:mt-2 mt-1 mr-1 sm:pt-[3px] lg:pt-0">
                <BackButton2 onClick={handleGoBack} />
              </div>
              <h1 className="h-[39px] md:pt-1 lg:pt-0 lg:ml-0 lg:mt-1 md:mt-[10px] mt-1 lg:mb-[20px] mb-[14px] text-[16px] sm:leading-[29.18px] md:text-base lg:text-base  font-medium">
                Integration
              </h1>
            </div>
          </div>
        )}

        <div>
          {showLoading ? (
            <>
              <div className="fixed inset-0 bg-black bg-opacity-50 z-40"></div>
              <div className="fixed inset-0 flex items-center justify-center z-50">
                <div className="loader border-t-4 border-[#006BEA] rounded-full w-12 h-12 animate-spin"></div>
              </div>
            </>
          ) : (
            <div className="lg:pl-6 pl-2 block">
              {integrationList.filter((info: any) => info.type === 'DATA_SOURCE')?.length !== 0 && (
                <>
                  <h1 className=" md:text-base  text-[14px] font-medium mt-2  sm:ml-[10px]">
                    Data Source
                  </h1>
                  <IntegrationDataCard
                    integrationList={integrationList.filter(
                      (info: any) => info.type === 'DATA_SOURCE'
                    )}
                    handleClick={handleModal}
                    handleDisconnect={handelToDisconnect}
                  />
                </>
              )}
              <h1 className=" md:text-base  text-[14px] font-medium mt-2 sm:ml-[4px] pl-1">
                CRM Integration
              </h1>
              <IntegrationDataCard
                integrationList={integrationList.filter((info: any) => info.type === 'CRM')}
                handleClick={handleCRMConnect}
                handleDisconnect={handelToDisconnect}
              />
              <h1 className=" md:text-base  text-[14px] font-medium mt-2 sm:ml-[4px] pl-1">
                Communication
              </h1>
              <IntegrationDataCard
                integrationList={integrationList.filter(
                  (info: any) => info.type === 'COMMUNICATION'
                )}
                handleClick={handleCRMConnect}
                handleDisconnect={handelToDisconnect}
              />
            </div>
          )}
        </div>

        {!isSettings ? (
          <div className="flex justify-end mt-auto lg:mb-3">
            <div className="mr-[10px]">
              <AccountSetupButton
                isFormValid={true}
                btnText="Skip"
                textColor="#FFFFFF"
                backGroundColor=""
                borderColor="#006BEA"
                onClick={handleMoveToDashbord}
              />
            </div>
            <div>
              <AccountSetupButton
                isFormValid={true}
                btnText={'Next'}
                textColor="#FFFFFF"
                backGroundColor="#006BEA"
                borderColor="#006BEA"
                onClick={handleMoveToDashbord}
              />
            </div>
          </div>
        ) : (
          <div className="pb-12" />
        )}

        {modalInfo.show && (
          <DataSourceInfoPopup
            submitDisabled={submitDisabled}
            modalInfo={modalInfo}
            setModalInfo={setModalInfo}
            onClose={() => handleModal('', '')}
            handleDataSourceConnect={handleDataSourceConnect}
            handleDeskCRMConnect={handleDeskCRMConnect}
          />
        )}
      </div>
    </>
  );
};
